import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Store } from '../Store';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { Helmet } from 'react-helmet-async';
import VoiceRecorder from '../components/VoiceRecorder';
import './DigitalMenu.css';
import MessageBox from '../components/MessageBox';

// Custom hook to get query parameters
function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function DigitalMenu() {
    const navigate = useNavigate();
    const query = useQuery();
    const tableId = query.get('table');
    const restaurantId = query.get('business'); // Get business ID from query
    const [tableInfo, setTableInfo] = useState(null);
    const [categories, setCategories] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [activeCategory, setActiveCategory] = useState('All');
    const [loading, setLoading] = useState(true);
    const [activeOrder, setActiveOrder] = useState(null);
    
    // Feedback/Claim State
    const [feedback, setFeedback] = useState('');
    const [submittingFeedback, setSubmittingFeedback] = useState(false);
    const [showRecorder, setShowRecorder] = useState(false);
    const [recordedAudio, setRecordedAudio] = useState(null);
    const [uploadedImage, setUploadedImage] = useState("");
    const [isUploading, setIsUploading] = useState(false);

    const [selectedItem, setSelectedItem] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [specialInstructions, setSpecialInstructions] = useState('');
    const [showItemModal, setShowItemModal] = useState(false);
    const { state, dispatch: ctxDispatch } = useContext(Store);
    const { cart, userInfo } = state;

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch table info if tableId exists
                if (tableId) {
                    try {
                        const { data: tableData } = await axios.get(`/api/tables/qr/${tableId}`);
                        setTableInfo(tableData);
                        localStorage.setItem('tableInfo', JSON.stringify(tableData));

                        // Check for active order for this table
                        try {
                            const { data: orderData } = await axios.get(`/api/orders/table/${tableData._id}/active`);
                            if (orderData.orderStatus === 'completed' || orderData.orderStatus === 'cancelled') {
                                setActiveOrder(null);
                            } else {
                                setActiveOrder(orderData);
                            }
                        } catch (err) {
                            setActiveOrder(null);
                        }
                    } catch (err) {
                        console.error('Table not found');
                    }
                }

                if (restaurantId) {
                    localStorage.setItem('currentBusinessId', restaurantId);
                }

                // Fetch categories
                const { data: categoriesData } = await axios.get('/api/menu/categories');
                setCategories(['All', ...categoriesData]);

                // Fetch menu items
                const { data: menuData } = await axios.get(`/api/menu?restaurantId=${restaurantId || ''}`);
                setMenuItems(menuData);
                setLoading(false);
            } catch (err) {
                setLoading(false);
                toast.error(getError(err));
            }
        };
        fetchData();

        // Poll for order status updates if there's an active order
        const interval = setInterval(() => {
            if (tableInfo?._id) {
                axios.get(`/api/orders/table/${tableInfo._id}/active`)
                    .then(({ data }) => {
                        if (data.orderStatus === 'completed' || data.orderStatus === 'cancelled') {
                            setActiveOrder(null);
                        } else {
                            setActiveOrder(data);
                        }
                    })
                    .catch(() => setActiveOrder(null));
            }
        }, 10000);

        return () => clearInterval(interval);
        // Check for active order on component mount and redirect if found
        const checkActiveOrder = async () => {
            if (tableId) {
                try {
                    const { data: orderData } = await axios.get(`/api/orders/table/${tableId}/active`);
                    if (orderData && orderData.orderStatus !== 'completed' && orderData.orderStatus !== 'cancelled') {
                        navigate(`/order/${orderData._id}`);
                    }
                } catch (err) {
                    // No active order, stay on menu
                }
            }
        };
        checkActiveOrder();
    }, [tableId, navigate]);

    const uploadFileHandler = async (file) => {
        if (!file) return null;
        try {
            setIsUploading(true);
            const bodyFormData = new FormData();
            
            // For audio blobs, we need to explicitly provide a filename
            if (file instanceof Blob && !(file instanceof File)) {
                const extension = file.type.includes('webm') ? 'webm' : 
                                 file.type.includes('ogg') ? 'ogg' : 
                                 file.type.includes('wav') ? 'wav' : 'bin';
                bodyFormData.append('file', file, `audio_record_${Date.now()}.${extension}`);
            } else {
                bodyFormData.append('file', file);
            }

            const { data } = await axios.post('/api/upload', bodyFormData, {
                headers: {
                    ...(userInfo ? { Authorization: `Bearer ${userInfo.token}` } : {}),
                },
            });
            setIsUploading(false);
            return data.secure_url;
        } catch (err) {
            console.error("Upload error:", err);
            toast.error(getError(err));
            setIsUploading(false);
            return null;
        }
    };

    const getDownloadUrl = (url) => {
        if (!url) return '';
        return url;
    };

    const submitFeedbackHandler = async (e) => {
        e.preventDefault();
        if (!feedback && !recordedAudio && !uploadedImage) {
            toast.error("Please provide some feedback (text, voice, or image)");
            return;
        }

        try {
            setSubmittingFeedback(true);

            let voiceUrl = "";
            if (recordedAudio) {
                voiceUrl = await uploadFileHandler(recordedAudio);
            }

            await axios.post(`/api/orders/${activeOrder._id}/feedback`, { 
                comment: feedback,
                voiceNote: voiceUrl,
                image: uploadedImage
            });
            
            toast.success('Feedback submitted');
            setFeedback('');
            setRecordedAudio(null);
            setUploadedImage("");
            setShowRecorder(false);
            setSubmittingFeedback(false);
            
            // Refresh active order to show new feedback immediately
            const { data } = await axios.get(`/api/orders/table/${tableInfo._id}/active`);
            setActiveOrder(data);

        } catch (err) {
            toast.error(getError(err));
            setSubmittingFeedback(false);
        }
    };

    const addToCartHandler = async () => {
        const existItem = cart.cartItems.find((x) => x._id === selectedItem._id);
        const quantityToAdd = existItem ? existItem.quantity + quantity : quantity;

        ctxDispatch({
            type: 'CART_ADD_ITEM',
            payload: {
                ...selectedItem,
                quantity: quantityToAdd,
                specialInstructions,
                product: selectedItem._id // Ensure compatibility with existing cart structure
            },
        });

        setShowItemModal(false);
        setQuantity(1);
        setSpecialInstructions('');
        toast.success('Added to order');
    };

    const openItemModal = (item) => {
        setSelectedItem(item);
        setQuantity(1);
        setSpecialInstructions('');
        setShowItemModal(true);
    };

    const filteredItems = activeCategory === 'All'
        ? menuItems
        : menuItems.filter(item => item.category === activeCategory);

    return (
        <div className="digital-menu">
            <Helmet>
                <title>Menu {tableInfo ? `- ${tableInfo.type === 'room' ? 'Room' : 'Table'} ${tableInfo.type === 'room' ? tableInfo.roomNumber : tableInfo.tableNumber}` : ''}</title>
            </Helmet>

            {/* Header */}
            <div className="menu-header bg-dark text-white p-3 mb-3 sticky-top">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="m-0">Restaurant Menu</h4>
                        {tableInfo && <small>{tableInfo.type === 'room' ? 'Room' : 'Table'}: {tableInfo.type === 'room' ? tableInfo.roomNumber : tableInfo.tableNumber}</small>}
                    </div>
                    <Button
                        variant="warning"
                        onClick={() => navigate('/cart')}
                        className="position-relative d-flex align-items-center"
                    >
                        <div className="me-2 text-end">
                            <div className="fw-bold" style={{ fontSize: '0.9rem' }}>Order</div>
                            <div className="small text-dark" style={{ fontSize: '0.75rem' }}>
                                {cart.cartItems.reduce((a, c) => a + c.price * c.quantity, 0)} RWF
                            </div>
                        </div>
                        <div className="position-relative">
                            <i className="fas fa-utensils"></i>
                            {cart.cartItems.length > 0 && (
                                <Badge bg="danger" className="position-absolute top-0 start-100 translate-middle rounded-circle" style={{ fontSize: '0.6rem' }}>
                                    {cart.cartItems.reduce((a, c) => a + c.quantity, 0)}
                                </Badge>
                            )}
                        </div>
                    </Button>
                </div>

                {/* Categories Scroller */}
                <div className="categories-wrapper mt-3 d-flex overflow-auto pb-2">
                    {categories.map((category) => (
                        <Button
                            key={category}
                            variant={activeCategory === category ? "light" : "outline-light"}
                            className="me-2 text-nowrap rounded-pill"
                            onClick={() => setActiveCategory(category)}
                            size="sm"
                        >
                            {category}
                        </Button>
                    ))}
                </div>
            </div>

            <Container className="mb-5 pb-5">
                {loading ? (
                    <div className="text-center mt-5">Loading...</div>
                ) : (
                    <>
                        {activeOrder && (
                            /* ACTIVE ORDER TRACKER SECTION */
                            <div className="order-tracker mt-2 mb-5">
                                <Card className="shadow-lg border-0 rounded-4 overflow-hidden mb-4">
                                    <Card.Body className="p-4 text-center">
                                        <div className="status-header mb-4">
                                            <div className={`display-4 mb-2 ${activeOrder.orderItems.some(i => i.status === 'preparing') ? 'pulse-preparing' : ''}`}>
                                                {activeOrder.orderItems.every(i => i.status === 'served') ? '🍽️' :
                                                    activeOrder.orderItems.some(i => i.status === 'ready') ? '🔔' :
                                                        activeOrder.orderItems.some(i => i.status === 'preparing') ? '👨‍🍳' : '⏳'}
                                            </div>
                                            <h4 className="fw-bold text-uppercase mb-1">
                                                {activeOrder.orderItems.every(i => i.status === 'served') ? 'Enjoy your meal!' :
                                                    activeOrder.orderItems.some(i => i.status === 'preparing') ? 'Kitchen is working' :
                                                        'Order Received'}
                                            </h4>
                                            <p className="text-muted small">{activeOrder.locationType === 'room' ? 'Room' : 'Table'} {activeOrder.tableNumber} • Order #{activeOrder._id.substring(activeOrder._id.length - 6)}</p>
                                        </div>

                                        <div className="d-flex justify-content-center mb-4">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="rounded-pill px-4"
                                                onClick={() => document.getElementById('menu-items-section').scrollIntoView({ behavior: 'smooth' })}
                                            >
                                                <i className="fas fa-arrow-down me-2"></i> Browse Menu to Add More
                                            </Button>
                                        </div>

                                        <Card className="bg-light border-0 rounded-3 text-start mt-4">
                                            <Card.Body className="p-3">
                                                {/* SERVED SECTION */}
                                                {activeOrder.orderItems.some(i => i.status === 'served') && (
                                                    <div className="status-group mb-3">
                                                        <h6 className="text-success fw-bold small mb-2 border-bottom pb-1"><i className="fas fa-check-circle me-1"></i> Already Served</h6>
                                                        <ListGroup variant="flush" className="bg-transparent mb-2">
                                                            {activeOrder.orderItems.filter(i => i.status === 'served').map(item => (
                                                                <ListGroup.Item key={item._id} className="bg-transparent px-0 py-1 d-flex justify-content-between align-items-center opacity-75">
                                                                    <span className="small">{item.quantity}x {item.name}</span>
                                                                    <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill x-small" style={{ fontSize: '0.6rem' }}>SERVED</span>
                                                                </ListGroup.Item>
                                                            ))}
                                                        </ListGroup>
                                                    </div>
                                                )}

                                                {/* READY / PREPARING SECTION */}
                                                {activeOrder.orderItems.some(i => ['preparing', 'ready'].includes(i.status)) && (
                                                    <div className="status-group mb-3">
                                                        <h6 className="text-primary fw-bold small mb-2 border-bottom pb-1"><i className="fas fa-fire me-1"></i> In the Kitchen</h6>
                                                        <ListGroup variant="flush" className="bg-transparent mb-2">
                                                            {activeOrder.orderItems.filter(i => ['preparing', 'ready'].includes(i.status)).map(item => (
                                                                <ListGroup.Item key={item._id} className="bg-transparent px-0 py-1 d-flex justify-content-between align-items-center">
                                                                    <span className="small fw-bold">{item.quantity}x {item.name}</span>
                                                                    <Badge bg={item.status === 'ready' ? 'success' : 'info'} className={`${item.status === 'preparing' ? 'pulse-preparing' : ''} x-small`} style={{ fontSize: '0.6rem' }}>
                                                                        {item.status.toUpperCase()}
                                                                    </Badge>
                                                                </ListGroup.Item>
                                                            ))}
                                                        </ListGroup>
                                                    </div>
                                                )}

                                                {/* PENDING SECTION */}
                                                {activeOrder.orderItems.some(i => ['pending', 'confirmed', undefined].includes(i.status)) && (
                                                    <div className="status-group">
                                                        <h6 className="text-muted fw-bold small mb-2 border-bottom pb-1"><i className="fas fa-clock me-1"></i> Waiting</h6>
                                                        <ListGroup variant="flush" className="bg-transparent">
                                                            {activeOrder.orderItems.filter(i => ['pending', 'confirmed', undefined].includes(i.status)).map(item => (
                                                                <ListGroup.Item key={item._id} className="bg-transparent px-0 py-1 d-flex justify-content-between align-items-center">
                                                                    <span className="small">{item.quantity}x {item.name}</span>
                                                                    <Badge bg="danger" className="x-small" style={{ fontSize: '0.6rem' }}>PENDING</Badge>
                                                                </ListGroup.Item>
                                                            ))}
                                                        </ListGroup>
                                                    </div>
                                                )}

                                                <div className="border-top mt-2 pt-2 d-flex justify-content-between fw-bold small">
                                                    <span>Total Price</span>
                                                    <span>{activeOrder.totalPrice} RWF</span>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Card.Body>
                                </Card>

                                {/* FEEDBACK SECTION */}
                                <Card className="shadow-lg border-0 rounded-4 mb-4">
                                    <Card.Body className="p-4">
                                        <h5 className="fw-bold mb-4 border-bottom pb-2">
                                            <i className="fas fa-comment-dots text-primary me-2"></i>
                                            Feedback & Claims
                                        </h5>
                                                                        {activeOrder.feedbacks && activeOrder.feedbacks.length > 0 ? (
                                            <div className="feedback-history mb-4" style={{ maxHeight: '400px', overflowY: 'auto', padding: '5px' }}>
                                                {activeOrder.feedbacks.map((f, i) => (
                                                    <div key={i} className="mb-4">
                                                        {/* Customer Message */}
                                                        <div className="d-flex flex-column align-items-start mb-2">
                                                            <div className="message-bubble customer-msg p-3 rounded-3 bg-light border-start border-4 border-primary" style={{ maxWidth: '90%' }}>
                                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                                    <span className="small fw-bold text-primary">You</span>
                                                                    <span className="x-small text-muted ms-3">{new Date(f.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                </div>
                                                                {f.comment && <p className="mb-2 small">{f.comment}</p>}
                                                                {f.image && (
                                                                    <div className="mb-2 position-relative d-inline-block">
                                                                        <img src={f.image} alt="Attachment" className="img-thumbnail" style={{ maxWidth: '100%', maxHeight: '150px' }} />
                                                                        <a href={getDownloadUrl(f.image)} download className="btn btn-sm btn-light position-absolute bottom-0 end-0 m-1 rounded-circle shadow-sm" title="Download">
                                                                            <i className="fas fa-download text-dark"></i>
                                                                        </a>
                                                                    </div>
                                                                )}
                                                                {f.voiceNote && (
                                                                    <div className="mt-1 d-flex align-items-center gap-2">
                                                                        <audio src={f.voiceNote} controls style={{ width: '100%', height: '45px' }} />
                                                                        <a href={getDownloadUrl(f.voiceNote)} download className="btn btn-sm btn-outline-secondary rounded-circle" title="Download">
                                                                            <i className="fas fa-download"></i>
                                                                        </a>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Manager Reply */}
                                                        {f.reply || f.replyVoiceNote || f.replyImage ? (
                                                            <div className="d-flex flex-column align-items-end mb-2">
                                                                <div className="message-bubble manager-msg p-3 rounded-3 bg-success-subtle border-end border-4 border-success" style={{ maxWidth: '90%' }}>
                                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                                        <span className="x-small text-muted me-3">{new Date(f.repliedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                        <span className="small fw-bold text-success">Manager</span>
                                                                    </div>
                                                                    {f.reply && <p className="mb-2 small">{f.reply}</p>}
                                                                    {f.replyImage && (
                                                                        <div className="mb-2 position-relative d-inline-block">
                                                                            <img src={f.replyImage} alt="Manager Attachment" className="img-thumbnail" style={{ maxWidth: '100%', maxHeight: '150px' }} />
                                                                            <a href={getDownloadUrl(f.replyImage)} download className="btn btn-sm btn-light position-absolute bottom-0 end-0 m-1 rounded-circle shadow-sm" title="Download">
                                                                                <i className="fas fa-download text-dark"></i>
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                    {f.replyVoiceNote && (
                                                                        <div className="mt-1 d-flex align-items-center gap-2">
                                                                            <audio src={f.replyVoiceNote} controls style={{ width: '100%', height: '45px' }} />
                                                                            <a href={getDownloadUrl(f.replyVoiceNote)} download className="btn btn-sm btn-outline-secondary rounded-circle" title="Download">
                                                                                <i className="fas fa-download"></i>
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="text-center mb-3">
                                                                <small className="text-muted fst-italic">Waiting for manager's response...</small>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <MessageBox variant="info">No claims or feedback submitted yet.</MessageBox>
                                        )}

                                        <Form onSubmit={submitFeedbackHandler} className="mt-3 pt-3 border-top">
                                            <Form.Group className="mb-3">
                                                <Form.Label className="small fw-bold">Raise a Claim / Follow up</Form.Label>
                                                
                                                {uploadedImage && (
                                                    <div className="position-relative mb-2" style={{ maxWidth: '100px' }}>
                                                        <img src={uploadedImage} alt="To upload" className="img-thumbnail" />
                                                        <Button 
                                                            variant="danger" 
                                                            size="sm" 
                                                            className="position-absolute top-0 end-0 rounded-circle p-0"
                                                            style={{ width: '20px', height: '20px', lineHeight: '1' }}
                                                            onClick={() => setUploadedImage("")}
                                                        >
                                                            &times;
                                                        </Button>
                                                    </div>
                                                )}

                                                {showRecorder ? (
                                                    <VoiceRecorder 
                                                        onRecordingComplete={(blob, url) => setRecordedAudio(blob)}
                                                        onCancel={() => {
                                                            setShowRecorder(false);
                                                            setRecordedAudio(null);
                                                        }}
                                                    />
                                                ) : (
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        placeholder="E.g. My order is taking long, or I need extra napkins..."
                                                        value={feedback}
                                                        onChange={(e) => setFeedback(e.target.value)}
                                                        className="border-0 bg-light rounded-3 mb-2"
                                                    />
                                                )}

                                                <div className="d-flex gap-2 mb-3">
                                                    {!showRecorder && (
                                                        <Button variant="outline-info" size="sm" onClick={() => setShowRecorder(true)}>
                                                            <i className="fas fa-microphone"></i> Record Voice
                                                        </Button>
                                                    )}
                                                    
                                                    <label className="btn btn-outline-info btn-sm m-0">
                                                        <i className="fas fa-camera"></i> {uploadedImage ? 'Change Photo' : 'Add Photo'}
                                                        <input 
                                                            type="file" 
                                                            hidden 
                                                            accept="image/*" 
                                                            onChange={async (e) => {
                                                                const url = await uploadFileHandler(e.target.files[0]);
                                                                if (url) setUploadedImage(url);
                                                            }} 
                                                        />
                                                    </label>
                                                </div>
                                            </Form.Group>
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                className="w-100 rounded-pill py-2"
                                                disabled={submittingFeedback || isUploading || (!feedback && !recordedAudio && !uploadedImage)}
                                            >
                                                {isUploading ? 'Uploading...' : submittingFeedback ? 'Submitting...' : 'Send Message'}
                                            </Button>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </div>
                        )}

                        {/* NORMAL MENU VIEW */}
                        <div id="menu-items-section">
                            <h5 className="mb-4 fw-bold">Select Items</h5>
                            <Row>
                                {filteredItems.map((item) => (
                                    <Col key={item._id} xs={12} md={6} lg={4} className="mb-4">
                                        <Card
                                            className="h-100 shadow-sm border-0 menu-item-card"
                                            onClick={() => openItemModal(item)}
                                            style={{ cursor: 'pointer', overflow: 'hidden' }}
                                        >
                                            <div className="row g-0 h-100">
                                                <div className="col-4">
                                                    <img
                                                        src={item.image}
                                                        alt={item.name}
                                                        className="img-fluid h-100 w-100"
                                                        style={{ objectFit: 'cover' }}
                                                    />
                                                </div>
                                                <div className="col-8">
                                                    <Card.Body className="p-3 d-flex flex-column h-100 justify-content-between">
                                                        <div>
                                                            <Card.Title className="h6 mb-1">{item.name}</Card.Title>
                                                            <Card.Text className="small text-muted mb-2 text-truncate-2">
                                                                {item.description}
                                                            </Card.Text>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                                            <span className="fw-bold text-primary">{item.price} RWF</span>
                                                            <Button variant="outline-primary" size="sm" className="rounded-circle btn-icon">
                                                                <i className="fas fa-plus"></i>
                                                            </Button>
                                                        </div>
                                                    </Card.Body>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                )) || <div className="text-center mt-5">No items found.</div>}
                            </Row>
                        </div>
                    </>
                )}
            </Container>

            {/* Item Detail Modal */}
            <Modal show={showItemModal} onHide={() => setShowItemModal(false)} centered>
                {selectedItem && (
                    <>
                        <div className="position-relative">
                            <img
                                src={selectedItem.image}
                                alt={selectedItem.name}
                                className="w-100"
                                style={{ height: '200px', objectFit: 'cover' }}
                            />
                            <Button
                                variant="light"
                                className="position-absolute top-0 end-0 m-2 rounded-circle shadow-sm"
                                onClick={() => setShowItemModal(false)}
                            >
                                <i className="fas fa-times"></i>
                            </Button>
                        </div>
                        <Modal.Body>
                            <h4>{selectedItem.name}</h4>
                            <p className="text-muted">{selectedItem.description}</p>
                            <h5 className="text-primary mb-3">{selectedItem.price} RWF</h5>

                            <Form.Group className="mb-3">
                                <Form.Label>Special Instructions</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="e.g., No onions, extra spicy..."
                                    value={specialInstructions}
                                    onChange={(e) => setSpecialInstructions(e.target.value)}
                                />
                            </Form.Group>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="fw-bold">Quantity</span>
                                <div className="d-flex align-items-center">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    >
                                        <i className="fas fa-minus"></i>
                                    </Button>
                                    <span className="mx-3 fw-bold">{quantity}</span>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setQuantity(quantity + 1)}
                                    >
                                        <i className="fas fa-plus"></i>
                                    </Button>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="primary"
                                className="w-100 py-2"
                                onClick={addToCartHandler}
                            >
                                Add to Order - {(selectedItem.price * quantity)} RWF
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </div>
    );
}